/**
 * ============================================================================
 *                              crmss 版权声明
 * ============================================================================
 * 本软件系统（crmss - 快速开发通用管理后台框架）所有代码版权归 crmss 团队所有。
 * 代码中的 crmss 版权声明不允许删除和修改。
 * ============================================================================
 * HTTP 请求工具 - 前后端接口自动对接
 * 基于 Axios 封装，支持热更新、自动加载、API签名
 */
import axios, { type AxiosInstance, type AxiosRequestConfig, type AxiosResponse } from 'axios'
import { ElMessage, ElMessageBox } from 'element-plus'
import { useUserStore } from '@/stores/user'
import { getSignatureHeaders, needSignature } from './signature'
import { isValidJwtFormat } from './jwt-check'

/**
 * crmss HTTP 请求工具标识
 */
export const CRMSS_REQUEST_FLAG = 'CRMSS_HTTP_REQUEST'

// crmss API 基础配置 - 自动对接后端
const CRMSS_API_CONFIG = {
  // 使用相对路径，让请求通过 Nginx 代理到后端
  baseURL: '/crmss-api',
  timeout: 30000,
  withCredentials: true,
  // crmss 请求头配置
  headers: {
    'Content-Type': 'application/json',
    'X-CRMSS-Client': 'web',
    'X-CRMSS-Version': '1.0.0'
  }
}

// 创建 Axios 实例
const request: AxiosInstance = axios.create({
  baseURL: CRMSS_API_CONFIG.baseURL,
  timeout: CRMSS_API_CONFIG.timeout,
  withCredentials: CRMSS_API_CONFIG.withCredentials,
  headers: CRMSS_API_CONFIG.headers
})

// 请求拦截器
request.interceptors.request.use(
  (config) => {
    // 添加 crmss 请求标识
    config.headers['X-CRMSS-Request'] = 'true'
    config.headers['X-CRMSS-Timestamp'] = Date.now().toString()
    
    // 添加 Token - 自动从 store 获取
    const userStore = useUserStore()
    if (userStore.token) {
      // 校验 token 是合法 JWT 格式（含两个 '.'），
      // 防止 "null" / "undefined" / 损坏字符串被当成 token 发给后端
      if (!isValidJwtFormat(userStore.token)) {
        console.warn('[crmss] 检测到非法 token 格式，清空并跳登录页:', userStore.token)
        userStore.clearToken()
        // 跳登录页（不弹 ElMessageBox，避免与响应拦截器的 401 流程冲突）
        if (location.pathname !== '/login') {
          location.href = '/login'
        }
        return Promise.reject(new Error('token 格式非法'))
      }
      config.headers['Authorization'] = `Bearer ${userStore.token}`
    }

    /** FormData 上传必须去掉默认 application/json，否则后端 consumes=multipart 会 415 */
    if (typeof FormData !== 'undefined' && config.data instanceof FormData) {
      const h = config.headers as any
      if (h && typeof h.delete === 'function') {
        h.delete('Content-Type')
      } else if (h) {
        delete h['Content-Type']
      }
    }

    // 添加 API 签名（仅对修改性请求）
    // 如果已经有签名头部，说明是重试请求，跳过签名生成
    if (needSignature(config.url || '', config.method || '') && !config.headers['X-Signature']) {
      // 将data转换为JSON字符串，确保签名使用的body与请求发送的body完全一致
      let rawBody = ''
      if (config.data && typeof config.data === 'object') {
        // 如果是 FormData，不使用 _body 参数（文件上传时后端无法读取 body）
        if (config.data instanceof FormData) {
          rawBody = ''
        } else {
          rawBody = JSON.stringify(config.data)
        }
      } else if (config.data && typeof config.data === 'string') {
        rawBody = config.data
      }
      const signatureHeaders = getSignatureHeaders({}, rawBody)
      Object.assign(config.headers, signatureHeaders)
      console.log('[crmss] API签名已添加:', signatureHeaders['X-Signature'], 'nonce:', signatureHeaders['X-Nonce'])
      console.log('[crmss] 签名body:', rawBody)
    } else if (config.headers['X-Signature']) {
      console.log('[crmss] 使用已有签名:', config.headers['X-Signature'], 'nonce:', config.headers['X-Nonce'])
    }
    
    // crmss 请求日志
    console.log(`[crmss] 请求: ${config.method?.toUpperCase()} ${config.url}`)
    
    return config
  },
  (error) => {
    console.error('[crmss] 请求错误:', error)
    return Promise.reject(error)
  }
)

// crmss 401 处理状态：防止并发请求重复弹窗 + 防止用户取消弹窗后 token 残留
let isHandling401 = false

/**
 * 统一处理 401（业务码 401 和 HTTP 401 共用同一套逻辑）
 * - 立刻清 token，阻止后续请求继续带坏 token
 * - 只弹一次 ElMessageBox，无论 confirm 还是 cancel 最终都跳 /login
 */
function handle401Logout(message: string) {
  if (isHandling401) return
  isHandling401 = true
  // 立刻清 token，阻止后续并发请求继续带坏 token
  try {
    const userStore = useUserStore()
    userStore.clearToken()
  } catch (e) {
    console.error('[crmss] 清除 token 失败:', e)
  }
  ElMessageBox.confirm(
    message || '登录状态已过期，请重新登录',
    '系统提示',
    {
      confirmButtonText: '重新登录',
      cancelButtonText: '取消',
      type: 'warning'
    }
  ).finally(() => {
    isHandling401 = false
    // 无论 confirm 还是 cancel，都强制跳登录页
    location.href = '/login'
  })
}

// 响应拦截器
request.interceptors.response.use(
  (response: AxiosResponse) => {
    const res = response.data

    // 401 优先处理（必须在 ElMessage.error 之前），避免重复弹窗
    if (res && res.code === 401) {
      // 签名错误走重试，不立即弹 401 弹窗
      if (res.message && (res.message.includes('签名') || res.message.includes('nonce'))) {
        const retryCount = (response.config as any).__retryCount || 0
        if (retryCount < 1) {
          ;(response.config as any).__retryCount = retryCount + 1
          if (needSignature(response.config.url || '', response.config.method || '')) {
            let rawBody = ''
            if (response.config.data && typeof response.config.data === 'object') {
              if (response.config.data instanceof FormData) {
                rawBody = ''
              } else {
                rawBody = JSON.stringify(response.config.data)
              }
            } else if (response.config.data && typeof response.config.data === 'string') {
              rawBody = response.config.data
            }
            const signatureHeaders = getSignatureHeaders({}, rawBody)
            delete response.config.headers['X-Timestamp']
            delete response.config.headers['X-Nonce']
            delete response.config.headers['X-Signature']
            Object.assign(response.config.headers, signatureHeaders)
            console.log('[crmss] 签名重试已生成:', signatureHeaders['X-Signature'])
          }
          return request(response.config)
        }
      }
      // 非签名错误 → 统一登出流程（只弹一次，立刻清 token）
      handle401Logout(res.message || '登录已过期，请重新登录')
      return Promise.reject(new Error(res.message || '登录已过期'))
    }

    // 其他非 200 业务错误
    if (res.code !== 200) {
      ElMessage.error(res.message || '请求失败')
      return Promise.reject(new Error(res.message || '请求失败'))
    }

    // crmss 响应日志
    console.log(`[crmss] 响应成功: ${response.config.url}`)

    return res
  },
  (error) => {
    const reqUrl = (error.config && error.config.url) || 'unknown'
    const reqMethod = (error.config && error.config.method) || 'unknown'
    console.error(`[crmss] 响应错误: ${(reqMethod || '').toUpperCase()} ${reqUrl}`, error)

    // 自动处理网络错误
    if (error.code === 'ECONNABORTED') {
      ElMessage.error('请求超时，请检查网络连接')
    } else if (error.response && error.response.status === 401) {
      // HTTP 401：统一走登出流程（与业务码 401 共用，防止重复弹窗）
      const data = error.response.data
      const serverMsg =
        data && typeof data === 'object' && (data.message || data.msg)
          ? String(data.message || data.msg).trim()
          : ''
      // 签名错误重试一次（不弹 401 弹窗）
      if (serverMsg && (serverMsg.includes('签名') || serverMsg.includes('nonce'))) {
        const retryCount = (error.config as any).__retryCount || 0
        if (retryCount < 1 && error.config) {
          ;(error.config as any).__retryCount = retryCount + 1
          if (needSignature(error.config.url || '', error.config.method || '')) {
            let rawBody = ''
            if (error.config.data && typeof error.config.data === 'object') {
              if (error.config.data instanceof FormData) {
                rawBody = ''
              } else {
                rawBody = JSON.stringify(error.config.data)
              }
            } else if (error.config.data && typeof error.config.data === 'string') {
              rawBody = error.config.data
            }
            const signatureHeaders = getSignatureHeaders({}, rawBody)
            delete error.config.headers['X-Timestamp']
            delete error.config.headers['X-Nonce']
            delete error.config.headers['X-Signature']
            Object.assign(error.config.headers, signatureHeaders)
            console.log('[crmss] 签名重试已生成:', signatureHeaders['X-Signature'])
            return request(error.config)
          }
        }
        ElMessage.error('请求签名验证失败，请刷新页面重试')
      } else {
        // 非签名错误的 401 → 立刻清 token + 弹一次 ElMessageBox + 跳登录
        handle401Logout(serverMsg || '登录已过期，请重新登录')
      }
    } else if (error.response) {
      const data = error.response.data
      const serverMsg =
        data && typeof data === 'object' && (data.message || data.msg)
          ? String(data.message || data.msg).trim()
          : ''
      if (serverMsg) {
        ElMessage.error(serverMsg)
      } else if (error.response.status === 500) {
        ElMessage.error(`服务器错误 (${(reqMethod || '').toUpperCase()} ${reqUrl})，请稍后重试`)
      } else {
        ElMessage.error(`请求失败 (${error.response.status}): ${(reqMethod || '').toUpperCase()} ${reqUrl}`)
      }
    } else {
      ElMessage.error(error.message || '网络错误')
    }

    return Promise.reject(error)
  }
)

// crmss 请求方法封装 - 支持热更新
export const crmssRequest = {
  get: <T = any>(url: string, config?: AxiosRequestConfig): Promise<T> => {
    return request.get(url, config)
  },
  
  post: <T = any>(url: string, data?: any, config?: AxiosRequestConfig): Promise<T> => {
    return request.post(url, data, config)
  },
  
  put: <T = any>(url: string, data?: any, config?: AxiosRequestConfig): Promise<T> => {
    return request.put(url, data, config)
  },
  
  delete: <T = any>(url: string, config?: AxiosRequestConfig): Promise<T> => {
    return request.delete(url, config)
  }
}

export default request
